import store from '@/store';
import { filterOutFreeTrialPayloads } from '@/common/service/free-trial';
import { flowTypes, pageConfig } from '@/common/service/page-config';

/**
 * Get usage type parameters from form or query
 * @param {string} usageTypeFromForm - Usage type from form
 * @returns {string} Usage type parameter
 */
const getUsageTypeParams = (usageTypeFromForm) => {
  if (usageTypeFromForm) {
    return usageTypeFromForm;
  }

  const usageTypeFromQuery = window.$query?.usageType;

  // Only for new subscription will reference usageType param from link
  const isNewSubscriptionFlow = [flowTypes.buyNow, flowTypes.basic2pro].includes(pageConfig?.type);
  if (isNewSubscriptionFlow && usageTypeFromQuery) {
    return usageTypeFromQuery;
  }

  return '';
};

/**
 * Class for generating coupon validation parameters
 */
class GenerateValidateCouponParams {
  constructor(options = {}) {
    this.options = options;
  }

  /**
   * Get filtered plan list excluding free trial payloads
   * @returns {Array} Filtered plan list
   */
  getPlanList() {
    return filterOutFreeTrialPayloads(store.getters.selectedPlanListPayLoad);
  }

  /**
   * Get extension parameters for coupon validation
   * @returns {Object} Extension parameters
   */
  getExtension() {
    const { soldToContact, partnerRefer, addressInfo } = store.getters;
    return {
      soldToCountry: soldToContact?.country,
      jointsaleCode: partnerRefer?.jointsaleCode,
      usageType: getUsageTypeParams(addressInfo?.usageType),
      promotionCode: pageConfig?.promoCampaignInfo?.promotion,
    };
  }

  /**
   * Generate parameters for coupon validation with full context
   * @param {Object} options - Options object
   * @param {string} options.couponCode - Coupon code to validate
   * @returns {Object} Validation parameters
   */
  generateValidateCouponParams({ couponCode = "" }) {
    const planList = this.getPlanList();
    const extension = this.getExtension();
    const oldPlanType = window.$config("oldPlanType");
    const couponPayload = store.getters.couponPayload;

    return {
      orderData: {
        planList,
        coupon: {
          ...couponPayload,
          code: couponCode,
        },
      },
      extension,
      oldPlanType,
    };
  }

  /**
   * Generate parameters for coupon verification
   * @param {Object} options - Options object
   * @param {string} options.couponCode - Coupon code to verify
   * @param {boolean} options.midTermFeature - Whether this is a mid-term feature coupon (for nonprofit discount re-verification)
   * @returns {Object} Verification parameters
   */
  generateVerifyCouponParams({ couponCode = '', midTermFeature = false }) {
    const planList = this.getPlanList();
    const extension = this.getExtension();

    const coupon = {
      fromCampaignLink: false,
      code: couponCode,
    };

    // Add midTermFeature flag for mid-contract re-verification scenarios
    if (midTermFeature) {
      coupon.midTermFeature = true;
    }

    return {
      orderData: {
        planList,
        coupon,
      },
      extension,
    };
  }

  /**
   * Generate parameters for pure coupon validation (minimal context)
   * @param {Object} options - Options object
   * @param {string} options.couponCode - Coupon code to validate
   * @returns {Object} Pure validation parameters
   */
  generatePureValidateCouponParams({ couponCode = '' }) {
    const planList = store.getters.selectedPlanListPayLoad;

    return {
      orderData: {
        planList,
        coupon: {
          code: couponCode,
        },
      },
      extension: {
        soldToCountry: store.getters?.soldToContact?.country,
      },
    };
  }

  /**
   * Generate parameters for free trial coupon validation
   * @param {Object} options - Options object
   * @param {string} options.couponCode - Coupon code to validate
   * @param {string} options.soldToCountry - Sold to country code
   * @returns {Object} Free trial validation parameters
   */
  generateFreeTrialValidateCouponParams({ couponCode = '', soldToCountry = '' }) {
    const planList = store.getters?.selectedPlanListPayLoad;

    return {
      orderData: {
        planList,
        coupon: {
          code: couponCode,
        },
      },
      extension: {
        soldToCountry: soldToCountry || store.getters?.soldToContact?.country,
      },
    };
  }
}

export const couponParamsGeneratorInstance = new GenerateValidateCouponParams();
